import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, Link, Outlet, useLocation } from "react-router-dom";
import "../../styles/AdminDashboard.css";
import "../../styles/userDashboard.css";
import logo from "../../logos/kodeit-logo.png";
import img1 from "../../assests/images/img1.png";
import {
  FiGrid,
  FiBookOpen,
  FiUpload,
  FiLogOut,
  FiChevronDown,
  FiChevronsLeft,
  FiChevronsRight,
  FiMenu,
  FiX,
  FiBell,
} from "react-icons/fi";
import axios from "../../axiosConfig";

const NAV_ITEMS = [
  {
    name: "Issued Books",
    icon: FiBookOpen,
    match: ["/user/dashboard/issued-books"],
    link: "/user/dashboard/issued-books",
  },
  {
    name: "Books Upload",
    icon: FiUpload,
    match: ["/user/dashboard/upload-book"],
    link: "/user/dashboard/upload-book",
  },
];

function pathMatches(pathname, prefixes) {
  return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function UserDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const dropdownRef = useRef(null);
  const isDashboardHome = location.pathname === "/user/dashboard";

  useEffect(() => {
    const savedState = localStorage.getItem("sidebarCollapsedUser");
    if (savedState !== null) setSidebarCollapsed(savedState === "true");

    const handleSidebarToggle = (event) => {
      if (event.detail && typeof event.detail.collapsed === "boolean") {
        setSidebarCollapsed(event.detail.collapsed);
      }
    };
    window.addEventListener("sidebar-toggle", handleSidebarToggle);
    return () => window.removeEventListener("sidebar-toggle", handleSidebarToggle);
  }, []);

  useEffect(() => {
    const next = {};
    NAV_ITEMS.forEach((item) => {
      if (pathMatches(location.pathname, item.match)) next[item.name] = true;
    });
    setOpenMenus((prev) => ({ ...prev, ...next }));
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!user?.user_id) return;
    const fetchUserDetails = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`/user/${user.user_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserDetails(res.data);
      } catch (error) {
        console.error("Failed to fetch user details:", error);
      }
    };
    fetchUserDetails();
  }, [user?.user_id]);

  const toggleMenu = (name) => {
    setOpenMenus((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const newState = !prev;
      localStorage.setItem("sidebarCollapsedUser", String(newState));
      return newState;
    });
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const initials = user?.name?.charAt(0).toUpperCase() || "U";

  return (
    <div className={`sa-app va-app ${sidebarCollapsed ? "is-collapsed" : ""} ${mobileOpen ? "is-mobile-open" : ""}`}>
      {mobileOpen && <div className="sa-backdrop" onClick={() => setMobileOpen(false)} />}

      <aside className="sa-sidebar">
        <div className="sa-brand">
          <Link to="/user/dashboard" className="sa-brand-link" onClick={() => setMobileOpen(false)}>
            <img src={logo} alt="Kodeit" className="sa-brand-logo" />
            {!sidebarCollapsed && (
              <div className="sa-brand-text">
                <span>Share Library</span>
              </div>
            )}
          </Link>
          <button className="sa-mobile-close" onClick={() => setMobileOpen(false)} type="button" aria-label="Close menu">
            <FiX />
          </button>
        </div>

        <div className="sa-nav-scroll">
          <p className="sa-nav-label">{sidebarCollapsed ? "" : "MENU"}</p>
          <ul className="sa-nav">
            <li>
              <Link
                to="/user/dashboard"
                className={`sa-nav-link ${isDashboardHome ? "is-active" : ""}`}
                onClick={() => setMobileOpen(false)}
              >
                <FiGrid />
                {!sidebarCollapsed && <span>Dashboard</span>}
              </Link>
            </li>

            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const groupActive = pathMatches(location.pathname, item.match);

              if (item.link) {
                return (
                  <li key={item.name}>
                    <Link
                      to={item.link}
                      className={`sa-nav-link ${groupActive ? "is-active" : ""}`}
                      onClick={() => setMobileOpen(false)}
                    >
                      <Icon />
                      {!sidebarCollapsed && <span>{item.name}</span>}
                    </Link>
                  </li>
                );
              }

              const isOpen = !!openMenus[item.name];
              return (
                <li key={item.name} className={`sa-nav-group ${groupActive ? "is-active-group" : ""}`}>
                  <button
                    type="button"
                    className={`sa-nav-link ${groupActive ? "is-active" : ""}`}
                    onClick={() => toggleMenu(item.name)}
                  >
                    <Icon />
                    {!sidebarCollapsed && (
                      <>
                        <span>{item.name}</span>
                        <FiChevronDown className={`sa-chevron ${isOpen ? "is-open" : ""}`} />
                      </>
                    )}
                  </button>
                  {!sidebarCollapsed && isOpen && (
                    <ul className="sa-subnav">
                      {item.subItems.map((sub) => (
                        <li key={sub.link}>
                          <Link
                            to={sub.link}
                            className={location.pathname === sub.link ? "is-active" : ""}
                            onClick={() => setMobileOpen(false)}
                          >
                            {sub.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>

          <p className="sa-nav-label">{sidebarCollapsed ? "" : "GENERAL"}</p>
          <ul className="sa-nav">
            <li>
              <button type="button" className="sa-nav-link sa-logout" onClick={handleLogout}>
                <FiLogOut />
                {!sidebarCollapsed && <span>Logout</span>}
              </button>
            </li>
          </ul>
        </div>

        <button type="button" className="sa-collapse-btn" onClick={toggleSidebar}>
          {sidebarCollapsed ? <FiChevronsRight /> : <FiChevronsLeft />}
          {!sidebarCollapsed && <span>Collapse sidebar</span>}
        </button>
      </aside>

      <div className="sa-shell">
        <header className="sa-header">
          <button type="button" className="sa-mobile-menu" onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <FiMenu />
          </button>

          <div className="sa-search va-search-spacer" aria-hidden="true" />

          <div className="sa-header-actions">
            <button type="button" className="sa-icon-btn" aria-label="Notifications">
              <FiBell />
            </button>

            <div
              className={`sa-user ${userDropdownOpen ? "is-open" : ""}`}
              onClick={() => setUserDropdownOpen((v) => !v)}
              ref={dropdownRef}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setUserDropdownOpen((v) => !v);
              }}
            >
              <span className="sa-avatar">{initials}</span>
              <div className="sa-user-copy">
                <strong>{userDetails?.name || user?.name || "User"}</strong>
                <span>{userDetails?.email || user?.email || "viewer"}</span>
              </div>
              {userDropdownOpen && (
                <div className="sa-dropdown sa-user-dropdown" onClick={(e) => e.stopPropagation()}>
                  <div className="sa-user-card">
                    <span className="sa-avatar lg">{initials}</span>
                    <div>
                      <strong>{userDetails?.name || user?.name || "User"}</strong>
                      <p>{userDetails?.email || user?.email || "—"}</p>
                    </div>
                  </div>
                  <button type="button" onClick={handleLogout}>
                    <FiLogOut /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="sa-content">
          {isDashboardHome ? (
            <div className="va-home">
              <div className="page-head">
                <div>
                  <h1 className="page-title">Your Books</h1>
                  <p className="page-desc">
                    Stay organized with your issued books, track access, and share feedback from one place.
                  </p>
                </div>
                <Link to="/user/dashboard/issued-books" className="btn btn-primary">
                  View Issued Books
                </Link>
              </div>
              <div className="panel va-home-panel">
                <div className="va-home-flex">
                  <div>
                    <h2>Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</h2>
                    <p>
                      Browse your assigned library, open books in the reader, and upload client inputs when needed.
                    </p>
                    <Link to="/user/dashboard/issued-books" className="btn btn-primary">
                      Open My Books
                    </Link>
                  </div>
                  <img src={img1} alt="Issued Books" className="va-home-img" />
                </div>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </div>
  );
}

export default UserDashboard;
